/**
 * DWS images — copy and constants, lifted verbatim from the mockup.
 *
 * DWS = Dimensioning and Weighing Scale. The courier's scanner records a
 * weight and three dimensions for a shipment; those readings are what a
 * weight freeze is argued from.
 *
 * DWS_STEPS rows 2-4 map onto DWS_SLOTS by key, so the resting page's shot
 * list and the upload form's drop zones cannot drift apart. That drift had
 * already happened once in the mockup: the form scored a fifth "package with
 * label" shot that the page never named, so a merchant who followed the page
 * exactly was marked down for a photograph nobody had asked them to take.
 * Adding or removing a slot changes the page copy too — they are meant to
 * agree, and any renderer built on top of this file should assert it.
 */
export const DWS_COURIERS = ['Delhivery', 'Bluedart', 'Xpressbees', 'Ekart', 'Shadowfax', 'DTDC', 'Blitz']

export const DWS_IMAGE_TYPES = {
  'image/jpeg': 'JPG', 'image/jpg': 'JPG', 'image/png': 'PNG', 'image/webp': 'WebP',
}

/* A packing video is the strongest evidence Prozo can file, so this screen
   takes it alongside the scale and dimension stills. Video is allowed a far
   larger budget because one continuous clip is the whole point of it. */
export const DWS_VIDEO_TYPES = {
  'video/mp4': 'MP4', 'video/quicktime': 'MOV', 'video/webm': 'WebM',
}

export const DWS_MAX_IMAGE_BYTES = 5 * 1024 * 1024   // 5 MB per image
export const DWS_MAX_VIDEO_BYTES = 80 * 1024 * 1024  // 80 MB per clip

export const DWS_SLOTS = [
  {k: 'len', label: 'Package length', hint: 'Tape along the long side'},
  {k: 'wid', label: 'Package width', hint: 'Tape across the width'},
  {k: 'hgt', label: 'Package height', hint: 'Tape up the height'},
  {k: 'scl', label: 'Shipment on weighing scale', hint: 'Display readable'},
  {k: 'lbl', label: 'Package with label', hint: 'AWB barcode in focus'},
]

export const DWS_STEPS = [
  {
    k: 'vid', icon: 'video', slots: null,
    title: 'Film one unbroken clip, ending on the scale',
    chip: 'Settles 97%', chipTone: 'ok',
    body: 'No cuts: the item going into its packing material, the parcel being sealed, then the sealed parcel on the scale with the reading legible. A courier scan cannot contradict a single take. On Prozo’s record video settles about 97% of freeze requests, images on their own about 52%.',
  },
  {
    k: 'dim', icon: 'box', slots: ['len', 'wid', 'hgt'],
    title: 'Photograph all three sides against a tape',
    body: 'Length, breadth and height, each readable in its own frame with the sealed parcel. Volumetric weight is billed from dimensions, so a volumetric over-charge can only be argued against a measured box.',
  },
  {
    k: 'scl', icon: 'package', slots: ['scl'],
    title: 'Photograph the sealed parcel on the scale',
    body: 'The display legible in the same shot as the parcel. Dead-weight over-charges are argued from the scale reading, not from the product weight.',
  },
  {
    k: 'lbl', icon: 'tags', slots: ['lbl'],
    title: 'Photograph the parcel with its label',
    body: 'The AWB barcode in focus. This is what ties your evidence to the shipment the courier billed you for; without it the record cannot be matched to a charge.',
  },
  {
    k: 'no', icon: 'ban', slots: [],
    title: 'Unpacked product shots are rejected',
    chip: 'Rejected', chipTone: 'danger',
    body: 'The courier bills the parcel it handled, not the item inside it, so a photograph of the product without its outer packing material proves nothing.',
  },
]

/** Verbatim. States that the 10-record cap is SHARED between the two sources. */
export const DWS_RETAIN = 'Each SKU keeps its 10 most recent records, the courier’s and yours together, for up to 6 months. Older ones drop off, so evidence you upload today will not last forever.'

/** Exact string required by the brief. It is the only em-dash in this module. */
export const DWS_API_TIP = 'Disabled — Prozo business decision.'

export const DWS_TWO_SOURCES = {
  lead: 'Two sources.',
  body: 'Sorter images are the courier’s own hub scan and arrive on their own. Merchant evidence is the packing video and images you upload here: only you can produce those, and no freeze can be raised without them.',
}

/**
 * Typed keys, not an assembled English sentence. The mockup joined gap
 * phrases with ', ' and used the result verbatim as footer text, tooltip AND
 * aria-label (map §7 R2) — which breaks silently under i18n and under a copy
 * edit. The keys drive logic; this map drives rendering, separately.
 */
export const MISSING_LABEL = {
  SKU: 'the SKU',
  EVIDENCE: 'the packing video or at least one image',
  AWB: 'the AWB',
  COURIER: 'the courier',
  WEIGHT: 'the weight the scale read',
}

export function mb(bytes) {
  return (bytes / 1048576).toFixed(1) + ' MB'
}

export function ext(name) {
  const i = String(name || '').lastIndexOf('.')
  return i > 0 ? name.slice(i + 1).toUpperCase() : 'unknown'
}
